import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Download,
  ExternalLink,
  Dna,
  Info,
  Layers,
  Copy,
  Check,
  Database,
  FlaskConical,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Skeleton } from '../components/ui/Skeleton'
import { MoleculeViewer3DWithErrorBoundary as MoleculeViewer3D, type DomainData, type SiteData, type PaeDomainData } from '../components/MoleculeViewer3D'
import { useProtein } from '../api/hooks'
import {
  getMibigUrl,
  getUniprotUrl,
  getNcbiCddUrl,
  getNcbiProteinUrl,
} from '../utils/externalLinks'
import { InlineCopy } from '../components/ui/CopyButton'

/**
 * Domain colors for 3D visualization
 * These match the colors used in the domain bar visualization
 */
const DOMAIN_COLORS_3D = [
  '#22d3ee', // cyan
  '#a855f7', // purple
  '#f59e0b', // amber
  '#22c55e', // green
  '#ec4899', // pink
  '#3b82f6', // blue
  '#ef4444', // red
  '#eab308', // yellow
]

// Types matching the backend API response
interface ConservedDomain {
  pssm_id: number
  accession: string
  short_name: string
  res_start: number
  res_end: number
  evalue: number
  seq_length: number
  cdd_url: string
  pdb_hit_count: number
  swissprot_hit_count: number
}

interface FunctionalSite {
  site_type: string
  residues: string
  source_domain: string
  seq_length?: number
}

interface StructureModel {
  filename: string
  source: 'alphafold' | 'colabfold'
  region_id?: number
  domain_id?: number
  res_start?: number
  res_end?: number
  download_url: string
}

interface ProteinDetailResponse {
  mibig_accession: string
  protein_id: string
  product?: string
  sequence?: string
  sequence_length: number
  uniprot_ids: string[]
  ncbi_url: string
  domains: ConservedDomain[]
  sites: FunctionalSite[]
  structures: StructureModel[]
  pdb_hit_count: number
  swissprot_hit_count: number
  fujisan_hit_count: number
  unkai_hit_count: number
}

export function ProteinViewerPage() {
  // Handle both /proteins/:geneId and /proteins/:clusterId/:proteinId routes
  const { geneId, clusterId, proteinId } = useParams<{ geneId?: string; clusterId?: string; proteinId?: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedStructure, setSelectedStructure] = useState<StructureModel | null>(null)
  const [pdbContent, setPdbContent] = useState<string | null>(null)
  const [structureLoading, setStructureLoading] = useState(false)
  const [copiedSequence, setCopiedSequence] = useState(false)

  // Use proteinId if available (from cluster detail page), otherwise use geneId
  const actualProteinId = proteinId || geneId || ''
  const mibigAccession = clusterId

  const { data: protein, isLoading: proteinLoading } = useProtein(actualProteinId, mibigAccession) as {
    data: ProteinDetailResponse | undefined
    isLoading: boolean
  }

  // Smart back navigation - goes to the previous page the user came from
  const handleBackNavigation = () => {
    const referrerPath = location.state?.from

    if (referrerPath) {
      // If we have a stored referrer, navigate to it
      navigate(referrerPath)
    } else if (window.history.length > 2) {
      // If browser history exists, go back
      navigate(-1)
    } else {
      // Fallback to cluster page
      navigate(`/clusters/${mibigAccession || protein?.mibig_accession}`)
    }
  }

  // Determine back button label based on where user came from
  const getBackButtonLabel = () => {
    const referrerPath = location.state?.from

    if (referrerPath) {
      if (referrerPath.includes('/blast')) return 'Back to BLAST Results'
      if (referrerPath.includes('/clusters') && !referrerPath.includes(mibigAccession || protein?.mibig_accession)) return 'Back to Clusters'
      if (referrerPath.includes('/search')) return 'Back to Search'
      if (referrerPath.includes('/reactions')) return 'Back to Reaction Search'
      if (referrerPath === '/') return 'Back to Home'
    }

    // Default to cluster
    return `Back to Cluster ${mibigAccession || protein?.mibig_accession}`
  }

  // Select first available structure when protein loads
  useEffect(() => {
    if (protein?.structures && protein.structures.length > 0 && !selectedStructure) {
      setSelectedStructure(protein.structures[0])
    }
  }, [protein, selectedStructure])

  // Load structure PDB content when selected structure changes
  useEffect(() => {
    if (selectedStructure?.download_url) {
      setStructureLoading(true)
      // Fetch raw PDB text directly (not via JSON API client)
      fetch(selectedStructure.download_url)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to fetch structure')
          }
          return response.text()
        })
        .then((pdbText) => {
          setPdbContent(pdbText)
        })
        .catch(() => {
          setPdbContent(null)
        })
        .finally(() => {
          setStructureLoading(false)
        })
    }
  }, [selectedStructure])

  const copySequence = async () => {
    if (protein?.sequence) {
      await navigator.clipboard.writeText(protein.sequence)
      setCopiedSequence(true)
      setTimeout(() => setCopiedSequence(false), 2000)
    }
  }

  const downloadPDB = () => {
    if (pdbContent && selectedStructure) {
      const blob = new Blob([pdbContent], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = selectedStructure.filename
      link.click()
      URL.revokeObjectURL(url)
    }
  }

  if (proteinLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[500px] w-full" />
      </div>
    )
  }

  if (!protein) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-white mb-4">Protein not found</h2>
        <Link to="/clusters">
          <Button variant="secondary" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Back to Browser
          </Button>
        </Link>
      </div>
    )
  }

  // Group structures by source
  const alphafoldStructures = protein.structures?.filter(s => s.source === 'alphafold') || []
  const colabfoldStructures = protein.structures?.filter(s => s.source === 'colabfold') || []

  // Prepare domain data for 3D viewer highlighting (no useMemo to avoid React error #310)
  const domainData: DomainData[] = (!protein?.domains || !Array.isArray(protein.domains))
    ? []
    : protein.domains.map((domain, idx) => ({
        name: String(domain.short_name || ''),
        resStart: Number(domain.res_start) || 0,
        resEnd: Number(domain.res_end) || 0,
        color: DOMAIN_COLORS_3D[idx % DOMAIN_COLORS_3D.length],
      }))

  // Prepare site data for 3D viewer highlighting (functional sites as sticks)
  const siteData: SiteData[] = (!protein?.sites || !Array.isArray(protein.sites))
    ? []
    : protein.sites.map((site) => ({
        siteType: String(site.site_type || ''),
        residues: String(site.residues || ''),
        sourceDomain: site.source_domain,
      }))

  // Prepare PAE domain data for the selected structure (ColabFold structures only)
  // PAE domains are represented as multiple entries in structures array with same filename
  // but different domain_id and res_start/res_end values
  const paeDomainData: PaeDomainData[] = (!protein?.structures || !selectedStructure)
    ? []
    : protein.structures
        .filter((s) =>
          s.filename === selectedStructure.filename &&
          s.domain_id !== null &&
          s.domain_id !== undefined &&
          s.res_start !== null &&
          s.res_end !== null
        )
        .map((s) => ({
          domainId: s.domain_id!,
          resStart: s.res_start!,
          resEnd: s.res_end!,
        }))

  // Determine if structure is from AlphaFold (for pLDDT coloring)
  const isAlphaFoldStructure = selectedStructure?.source === 'alphafold'

  return (
    <div className="space-y-6">
      {/* Smart Back Navigation */}
      <button
        onClick={handleBackNavigation}
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {getBackButtonLabel()}
      </button>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-start justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-white font-mono">
            <InlineCopy text={protein.protein_id}>
              {protein.protein_id}
            </InlineCopy>
          </h1>
          <p className="text-xl text-gray-400 mt-1">
            {protein.product || 'No description available'}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge variant="purple" size="md">
              {protein.sequence_length} amino acids
            </Badge>
            <a
              href={getMibigUrl(protein.mibig_accession)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Badge variant="cyan" size="md" className="hover:bg-accent-cyan/30 cursor-pointer">
                {protein.mibig_accession}
              </Badge>
            </a>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a
            href={getNcbiProteinUrl(protein.protein_id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="secondary" leftIcon={<ExternalLink className="w-4 h-4" />}>
              NCBI Protein
            </Button>
          </a>
          {protein.uniprot_ids && protein.uniprot_ids.length > 0 && (
            <a
              href={getUniprotUrl(protein.uniprot_ids[0])}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="secondary" leftIcon={<ExternalLink className="w-4 h-4" />}>
                UniProt
              </Button>
            </a>
          )}
          <Button
            variant="secondary"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={downloadPDB}
            disabled={!pdbContent}
          >
            Download PDB
          </Button>
        </div>
      </motion.div>

      {/* UniProt IDs */}
      {protein.uniprot_ids && protein.uniprot_ids.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-gray-400 text-sm">UniProt:</span>
          {protein.uniprot_ids.map((uid, i) => (
            <InlineCopy key={i} text={uid}>
              <a
                href={getUniprotUrl(uid)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-cyan hover:underline font-mono text-sm"
              >
                {uid}
              </a>
            </InlineCopy>
          ))}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 3D Viewer */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-accent-cyan" />
                  3D Structure
                </CardTitle>
                <div className="flex gap-2 flex-wrap">
                  {protein.structures && protein.structures.length > 0 && (
                    <select
                      value={selectedStructure?.filename || ''}
                      onChange={(e) => {
                        const struct = protein.structures?.find(s => s.filename === e.target.value)
                        if (struct) setSelectedStructure(struct)
                      }}
                      className="px-3 py-1.5 text-sm rounded-lg bg-white/5 border border-white/10 text-white"
                    >
                      {alphafoldStructures.length > 0 && (
                        <optgroup label="AlphaFold">
                          {alphafoldStructures.map((s) => (
                            <option key={s.filename} value={s.filename} style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>
                              {s.filename.replace('.pdb', '')}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {colabfoldStructures.length > 0 && (
                        <optgroup label="ColabFold">
                          {colabfoldStructures.map((s) => (
                            <option key={s.filename} value={s.filename} style={{ backgroundColor: '#1a1a24', color: '#f3f4f6' }}>
                              {s.filename.replace('.pdb', '')}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {structureLoading ? (
                <div className="h-[500px] bg-white/5 rounded-xl animate-pulse flex items-center justify-center">
                  <p className="text-gray-500">Loading structure...</p>
                </div>
              ) : pdbContent ? (
                <MoleculeViewer3D
                  pdbData={pdbContent}
                  height="500px"
                  domains={domainData}
                  sites={siteData}
                  paeDomains={paeDomainData}
                  showDomainLabels={true}
                  colorScheme={isAlphaFoldStructure ? 'plddt' : 'secondary'}
                />
              ) : (
                <div className="h-[500px] bg-white/5 rounded-xl flex items-center justify-center">
                  <p className="text-gray-500">No structure available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* BLAST Hit Counts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-accent-amber" />
                BLAST Hits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link
                to={`/proteins/${mibigAccession || protein.mibig_accession}/${protein.protein_id}/pdb-hits`}
                className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
              >
                <span className="text-gray-300">PDB Hits</span>
                <Badge variant={protein.pdb_hit_count > 0 ? 'amber' : 'default'}>
                  {protein.pdb_hit_count}
                </Badge>
              </Link>
              <Link
                to={`/proteins/${mibigAccession || protein.mibig_accession}/${protein.protein_id}/swissprot-hits`}
                className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
              >
                <span className="text-gray-300">SwissProt Hits</span>
                <Badge variant={protein.swissprot_hit_count > 0 ? 'purple' : 'default'}>
                  {protein.swissprot_hit_count}
                </Badge>
              </Link>
            </CardContent>
          </Card>

          {/* Function Prediction Hits */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-accent-green" />
                Function Prediction
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link
                to={`/proteins/${mibigAccession || protein.mibig_accession}/${protein.protein_id}/fujisan-hits`}
                className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
              >
                <span className="text-gray-300">FUJISAN Hits</span>
                <Badge variant={protein.fujisan_hit_count > 0 ? 'cyan' : 'default'}>
                  {protein.fujisan_hit_count}
                </Badge>
              </Link>
              <Link
                to={`/proteins/${mibigAccession || protein.mibig_accession}/${protein.protein_id}/unkai-hits`}
                className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
              >
                <span className="text-gray-300">UNKAI Hits</span>
                <Badge variant={protein.unkai_hit_count > 0 ? 'green' : 'default'}>
                  {protein.unkai_hit_count}
                </Badge>
              </Link>
            </CardContent>
          </Card>

          {/* Sequence */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Dna className="w-5 h-5 text-accent-green" />
                  Sequence
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={copiedSequence ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  onClick={copySequence}
                >
                  {copiedSequence ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {protein.sequence ? (
                <pre className="text-xs text-gray-400 font-mono whitespace-pre-wrap break-all bg-white/5 p-3 rounded-lg max-h-48 overflow-y-auto">
                  {protein.sequence}
                </pre>
              ) : (
                <p className="text-gray-500">No sequence available</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Conserved Domains Section */}
      {protein.domains && protein.domains.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-accent-purple" />
              Conserved Domains ({protein.domains.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">PSSM ID</th>
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">Accession</th>
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">Short Name</th>
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">Residue Range</th>
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">E-value</th>
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">PDB Hits</th>
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">SwissProt Hits</th>
                  </tr>
                </thead>
                <tbody>
                  {protein.domains.map((domain, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4">
                        <a
                          href={getNcbiCddUrl(domain.pssm_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-accent-cyan hover:underline"
                        >
                          {domain.pssm_id}
                        </a>
                      </td>
                      <td className="py-3 px-4 font-mono text-gray-300">{domain.accession}</td>
                      <td className="py-3 px-4 text-white">{domain.short_name}</td>
                      <td className="py-3 px-4 text-gray-400">
                        {domain.res_start} - {domain.res_end}
                      </td>
                      <td className="py-3 px-4 text-gray-400 font-mono">
                        {domain.evalue.toExponential(2)}
                      </td>
                      <td className="py-3 px-4">
                        {domain.pdb_hit_count > 0 ? (
                          <Link
                            to={`/proteins/${mibigAccession || protein.mibig_accession}/${protein.protein_id}/pdb-hits?res_start=${domain.res_start}&res_end=${domain.res_end}`}
                            className="text-accent-amber hover:underline"
                          >
                            {domain.pdb_hit_count}
                          </Link>
                        ) : (
                          <span className="text-gray-500">0</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {domain.swissprot_hit_count > 0 ? (
                          <Link
                            to={`/proteins/${mibigAccession || protein.mibig_accession}/${protein.protein_id}/swissprot-hits?res_start=${domain.res_start}&res_end=${domain.res_end}`}
                            className="text-accent-purple hover:underline"
                          >
                            {domain.swissprot_hit_count}
                          </Link>
                        ) : (
                          <span className="text-gray-500">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Domain visualization bar */}
            <div className="mt-6">
              <p className="text-sm text-gray-400 mb-2">Domain positions on sequence</p>
              <div className="relative h-8 bg-white/5 rounded-lg overflow-hidden">
                {protein.domains.map((domain, idx) => {
                  const leftPercent = (domain.res_start / protein.sequence_length) * 100
                  const widthPercent = ((domain.res_end - domain.res_start) / protein.sequence_length) * 100
                  const colors = ['bg-accent-cyan', 'bg-accent-purple', 'bg-accent-amber', 'bg-accent-green']
                  return (
                    <div
                      key={idx}
                      className={`absolute h-full ${colors[idx % colors.length]} opacity-70 hover:opacity-100 transition-opacity cursor-pointer`}
                      style={{
                        left: `${leftPercent}%`,
                        width: `${widthPercent}%`,
                      }}
                      title={`${domain.short_name} (${domain.res_start}-${domain.res_end})`}
                    />
                  )
                })}
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1</span>
                <span>{protein.sequence_length}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Functional Sites Section */}
      {protein.sites && protein.sites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-accent-green" />
              Functional Sites ({protein.sites.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">Site Type</th>
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">Residues</th>
                    <th className="text-left py-2 px-4 text-gray-400 font-medium">Source Domain</th>
                  </tr>
                </thead>
                <tbody>
                  {protein.sites.map((site, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white">{site.site_type}</td>
                      <td className="py-3 px-4 font-mono text-accent-cyan">{site.residues}</td>
                      <td className="py-3 px-4 text-gray-400">{site.source_domain}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Structures Download */}
      {protein.structures && protein.structures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-accent-cyan" />
              Download Coordinates ({protein.structures.length} models)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {protein.structures.map((structure, idx) => (
                <a
                  key={idx}
                  href={structure.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Layers className="w-5 h-5 text-accent-cyan flex-shrink-0" />
                  <div className="overflow-hidden">
                    <p className="text-white text-sm truncate">{structure.filename}</p>
                    <p className="text-gray-500 text-xs">
                      {structure.source === 'alphafold' ? 'AlphaFold' : 'ColabFold'}
                      {structure.res_start && structure.res_end && ` (${structure.res_start}-${structure.res_end})`}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
